import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert, Typography, Tag, Button, Input, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { avaliacoesValidacaoService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Contrato real: GET /api/avaliacoes/validacao/{id} (DetalheParaValidacaoResponse)
// -- exatamente o que será exibido ao responsável após a validação.
// Mesma terminologia amigável de nível já usada no Portal do
// Professor/Responsável -- o valor cru nunca aparece.
const NIVEL_INFO = {
  EM_DESENVOLVIMENTO: { label: 'Em desenvolvimento', color: '#d48806' },
  ADEQUADO: { label: 'Adequado', color: '#1677ff' },
  BOM: { label: 'Bom', color: '#389e0d' },
  AVANCA_ALEM_DO_ESPERADO: { label: 'Avança além do esperado', color: '#722ed1' },
};

const ORDEM_GRUPOS = ['PARTICIPACAO', 'COMPORTAMENTO', 'FREQUENCIA', 'DESENVOLVIMENTO_TECNICO'];
const GRUPO_LABEL = {
  PARTICIPACAO: 'Participação',
  COMPORTAMENTO: 'Comportamento',
  FREQUENCIA: 'Frequência',
  DESENVOLVIMENTO_TECNICO: 'Desenvolvimento Técnico',
};

const agruparCriteriosPorGrupo = (criterios) => {
  const porGrupo = new Map();
  criterios.forEach((c) => {
    if (!porGrupo.has(c.grupo)) porGrupo.set(c.grupo, []);
    porGrupo.get(c.grupo).push(c);
  });
  return ORDEM_GRUPOS.filter((g) => porGrupo.has(g)).map((g) => ({ grupo: g, itens: porGrupo.get(g) }));
};

const MENSAGEM_NAO_ENCONTRADA = 'Esta avaliação não está mais disponível para validação.';

const DetalheValidacaoModal = ({ open, avaliacaoAlunoId, onClose, onAcaoConcluida }) => {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null); // { status, message } | null
  const [validando, setValidando] = useState(false);
  const [modalAjusteAberto, setModalAjusteAberto] = useState(false);
  const [motivoAjuste, setMotivoAjuste] = useState('');
  const [enviandoAjuste, setEnviandoAjuste] = useState(false);

  const carregar = async () => {
    if (!avaliacaoAlunoId) return;
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesValidacaoService.obterDetalhe(avaliacaoAlunoId);
      setDetalhe(response.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 409) {
        setErro({ status, message: MENSAGEM_NAO_ENCONTRADA });
      } else {
        const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar esta avaliação.');
        setErro({ status, message: mensagem });
      }
      setDetalhe(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      carregar();
    } else {
      setDetalhe(null);
      setErro(null);
      setModalAjusteAberto(false);
      setMotivoAjuste('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, avaliacaoAlunoId]);

  const handleValidar = () => {
    Modal.confirm({
      title: 'Validar e publicar esta avaliação?',
      content: 'Depois de validada, esta avaliação ficará disponível ao responsável imediatamente e não poderá mais ser editada pelo professor.',
      okText: 'Validar e publicar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setValidando(true);
        try {
          await avaliacoesValidacaoService.validar(avaliacaoAlunoId);
          message.success('Avaliação validada e publicada com sucesso.');
          onAcaoConcluida?.(avaliacaoAlunoId);
          onClose();
        } catch (err) {
          const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível validar esta avaliação.');
          message.error(mensagem);
        } finally {
          setValidando(false);
        }
      },
    });
  };

  const handleConfirmarAjuste = async () => {
    const motivoNormalizado = motivoAjuste.trim();
    if (!motivoNormalizado) {
      message.warning('Informe o motivo do ajuste.');
      return;
    }
    setEnviandoAjuste(true);
    try {
      await avaliacoesValidacaoService.solicitarAjuste(avaliacaoAlunoId, motivoNormalizado);
      message.success('Ajuste solicitado. A avaliação voltou para o professor.');
      setModalAjusteAberto(false);
      onAcaoConcluida?.(avaliacaoAlunoId);
      onClose();
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível solicitar o ajuste.');
      message.error(mensagem);
    } finally {
      setEnviandoAjuste(false);
    }
  };

  return (
    <>
      <Modal
        title={detalhe ? `${detalhe.nome_aluno} — ${detalhe.nome_periodo}` : 'Avaliação'}
        open={open}
        onCancel={onClose}
        width={560}
        footer={
          detalhe && !erro
            ? [
                <Button key="fechar" onClick={onClose}>
                  Fechar
                </Button>,
                <Button key="ajuste" onClick={() => setModalAjusteAberto(true)} disabled={validando}>
                  Solicitar ajuste
                </Button>,
                <Button key="validar" type="primary" loading={validando} onClick={handleValidar}>
                  Validar e publicar
                </Button>,
              ]
            : [
                <Button key="fechar" onClick={onClose}>
                  Fechar
                </Button>,
              ]
        }
      >
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin />
          </div>
        )}

        {!loading && erro && (
          <Alert
            type={erro.status === 404 || erro.status === 409 ? 'warning' : 'error'}
            showIcon
            message="Não foi possível abrir"
            description={erro.message}
            action={
              erro.status !== 404 && erro.status !== 409 ? (
                <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                  Tentar novamente
                </Button>
              ) : undefined
            }
          />
        )}

        {!loading && !erro && detalhe && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Text type="secondary">
              {detalhe.nome_turma} • {detalhe.nome_modalidade} • Professor(a): {detalhe.nome_equipe}
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {agruparCriteriosPorGrupo(detalhe.criterios).map(({ grupo, itens }) => (
                <div key={grupo} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Text strong type="secondary">
                    {GRUPO_LABEL[grupo] || grupo}
                  </Text>
                  {itens.map((criterio, indice) => {
                    const nivelInfo = NIVEL_INFO[criterio.nivel] || {
                      label: criterio.nivel || 'Sem resposta',
                      color: 'default',
                    };
                    return (
                      <div key={`${criterio.nome}-${indice}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Text strong>{criterio.nome}</Text>
                        {criterio.descricao && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {criterio.descricao}
                          </Text>
                        )}
                        <Tag color={nivelInfo.color} style={{ width: 'fit-content' }}>
                          {nivelInfo.label}
                        </Tag>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {detalhe.comentario && detalhe.comentario.trim() !== '' && (
              <div>
                <Title level={5} style={{ marginBottom: 4 }}>
                  Observações do professor
                </Title>
                <Text>{detalhe.comentario}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Solicitar ajuste"
        open={modalAjusteAberto}
        onCancel={() => setModalAjusteAberto(false)}
        onOk={handleConfirmarAjuste}
        okText="Solicitar ajuste"
        cancelText="Cancelar"
        confirmLoading={enviandoAjuste}
      >
        <Text>
          A avaliação voltará para o professor corrigir. Descreva o que precisa ser ajustado.
        </Text>
        <div style={{ marginTop: 12 }}>
          <Text strong id="motivo-ajuste-label">
            Motivo do ajuste *
          </Text>
          <TextArea
            aria-labelledby="motivo-ajuste-label"
            value={motivoAjuste}
            onChange={(e) => setMotivoAjuste(e.target.value)}
            placeholder="Explique o que precisa ser corrigido"
            autoSize={{ minRows: 3, maxRows: 6 }}
            maxLength={2000}
            style={{ marginTop: 4 }}
          />
        </div>
      </Modal>
    </>
  );
};

export default DetalheValidacaoModal;
